import { BoothToggleStep } from './BoothToggleStep';
import { AvailableExpertStep } from 'FRONTEND/components/Calendar/AppointmentForm/AvailableExpertStep';
import { AvailableTypeStep } from 'FRONTEND/components/Calendar/AppointmentForm/AvailableTypeStep';
import { DateTimeStep } from 'FRONTEND/components/Calendar/AppointmentForm/DateTimeStep';
import { ExpertStep } from 'FRONTEND/components/Calendar/AppointmentForm/ExpertStep';
import { LocationStep } from 'FRONTEND/components/Calendar/AppointmentForm/LocationStep';
import { MultipleAvailableExpertStep } from 'FRONTEND/components/Calendar/AppointmentForm/MultipleAvailableExpertStep';
import { MultipleExpertStep } from 'FRONTEND/components/Calendar/AppointmentForm/MultipleExpertStep';
import { NotifCheckboxReasonStep } from 'FRONTEND/components/Calendar/AppointmentForm/NotifCheckboxReasonStep';
import { PatientStep } from 'FRONTEND/components/Calendar/AppointmentForm/PatientStep';
import { PaymentExtraStep } from 'FRONTEND/components/Calendar/AppointmentForm/PaymentExtraStep';
import { PaymentToggleStep } from 'FRONTEND/components/Calendar/AppointmentForm/PaymentToggleStep';
import { ReasonStep } from 'FRONTEND/components/Calendar/AppointmentForm/ReasonStep';
import { SlotStep } from 'FRONTEND/components/Calendar/AppointmentForm/SlotStep';
import { TypeStep } from 'FRONTEND/components/Calendar/AppointmentForm/TypeStep';
import {
  FormTypes,
  VIDEO_CONSULTATION,
  VIDEO_SPECIALIST_ASSESSMENT,
} from 'FRONTEND/utils/constants';
import { getAvailableExpertSupport } from 'FRONTEND/utils/support/availableExpertSupport';
import { getDateTimeStepSupport } from 'FRONTEND/utils/support/dateTimeSupport';
import { getExpertStepSupport } from 'FRONTEND/utils/support/expertSupport';
import { getLocationStepSupport } from 'FRONTEND/utils/support/locationSupport';
import { getMultipleAvailableExpertStepSupport } from 'FRONTEND/utils/support/multipleAvailableExpertSupport';
import { getMultipleExpertStepSupport } from 'FRONTEND/utils/support/multipleExpertSupport';
import { getPatientSupport } from 'FRONTEND/utils/support/patientSupport';
import { getPaymentSupport } from 'FRONTEND/utils/support/paymentSupport';
import { getReasonStepSupport } from 'FRONTEND/utils/support/reasonSupport';
import { getRequesterStepSupport } from 'FRONTEND/utils/support/requesterSupport';
import { getSlotStepSupport } from 'FRONTEND/utils/support/slotSupport';
import { getTypeStepSupport } from 'FRONTEND/utils/support/typeSupport';

/**
 * Computes a `stepList` according to the type of form and the type of appointment that is used.
 * A condition list is internally attached to the computed step list. The first falsy condition of the list defines the `initialStepIndex` and is returned alongside the step list.
 * A `resetStepIndex` is also returned and is pointing to the step index to use in case of form reset.
 *
 * @param formType
 * @param appointmentType
 * @param [conditionMap]
 * @param [conditionMap.selectedExpert]
 * @param [conditionMap.additionalSelectedExpertList]
 * @param [conditionMap.selectedType]
 * @param [conditionMap.selectedPatient]
 * @param [conditionMap.selectedSlot]
 * @param [conditionMap.desiredDateTime]
 * @param [conditionMap.desiredDuration]
 * @returns {{
 *   initialStepIndex: number,
 *   stepList: AppointmentFormStep,
 *   resetStepIndex: number,
 * }}
 */
export const getFormStepListAndIndex = (formType, appointmentType = VIDEO_CONSULTATION, {
  selectedExpert,
  additionalSelectedExpertList,
  availableTypeList,
  selectedType,
  selectedPatient,
  selectedLocation,
  selectedSlot,
  desiredDateTime,
  desiredDuration,
  expertHasStripe,
  hasExpertConsultationCodes,
  hasPayment,
  showLocationStep,
  desiredConsultationCode,
  isPayLater,
  isBooth,
} = {}) => {
  let stepConditionList = [];
  let stepList = [];
  const isBoothToggleStepValidated = typeof isBooth === 'boolean';
  const isPaymentToggleStepValidated = typeof hasPayment === 'boolean';
  switch (formType) {
    case FormTypes.GIVEN_EXPERT_FORM:
      if (appointmentType !== VIDEO_SPECIALIST_ASSESSMENT) {
        stepConditionList = [
          selectedExpert,
          (expertHasStripe && hasExpertConsultationCodes) ? isPaymentToggleStepValidated : 'SKIPPED', // distinguish with string so we can filter, because these are booleans
          selectedType,
          selectedPatient,
          selectedSlot,
        ].filter(condition => condition !== 'SKIPPED');
        stepList = [
          { StepComponent: ExpertStep, supportContent: getExpertStepSupport(), isLocked: true },
          (expertHasStripe && hasExpertConsultationCodes) && { StepComponent: PaymentToggleStep, supportContent: getPaymentSupport(), isAfterExpertStep: true },
          { StepComponent: AvailableTypeStep, supportContent: getTypeStepSupport(), isLocked: availableTypeList.length === 1 },
          { StepComponent: PatientStep, supportContent: getPatientSupport() },
          { StepComponent: SlotStep, supportContent: getSlotStepSupport() },
          { StepComponent: appointmentType === VIDEO_CONSULTATION ? ReasonStep : NotifCheckboxReasonStep, supportContent: getReasonStepSupport() },
        ].filter(Boolean);
      } else {
        stepConditionList = [
          selectedExpert,
          (expertHasStripe && hasExpertConsultationCodes) ? isPaymentToggleStepValidated : 'SKIPPED',
          selectedType,
          selectedPatient,
          additionalSelectedExpertList?.length > 0,
          selectedSlot,
        ].filter(condition => condition !== 'SKIPPED');
        stepList = [
          { StepComponent: ExpertStep, supportContent: getRequesterStepSupport(), isLocked: true },
          (expertHasStripe && hasExpertConsultationCodes) && { StepComponent: PaymentToggleStep, supportContent: getPaymentSupport(), isAfterExpertStep: true },
          { StepComponent: AvailableTypeStep, supportContent: getTypeStepSupport(), isLocked: availableTypeList.length === 1 },
          { StepComponent: PatientStep, supportContent: getPatientSupport() },
          { StepComponent: MultipleExpertStep, supportContent: getMultipleExpertStepSupport() },
          { StepComponent: SlotStep, supportContent: getSlotStepSupport() },
          { StepComponent: ReasonStep, supportContent: getReasonStepSupport() },
        ].filter(Boolean);
      }
      break;
    case FormTypes.GIVEN_SLOT_FORM:
      if (appointmentType !== VIDEO_SPECIALIST_ASSESSMENT) {
        stepConditionList = [
          selectedType,
          selectedPatient,
          desiredDateTime,
          isPaymentToggleStepValidated,
          selectedExpert,
          hasPayment && hasExpertConsultationCodes ? Boolean(desiredConsultationCode) || isPayLater : 'SKIPPED', // only if payment is enabled
        ].filter(condition => condition !== 'SKIPPED');
        stepList = [
          { StepComponent: TypeStep, supportContent: getTypeStepSupport() },
          { StepComponent: PatientStep, supportContent: getPatientSupport() },
          { StepComponent: DateTimeStep, supportContent: getDateTimeStepSupport() },
          { StepComponent: PaymentToggleStep, supportContent: getPaymentSupport(), isAfterExpertStep: false },
          { StepComponent: AvailableExpertStep, supportContent: getAvailableExpertSupport() },
          hasPayment && hasExpertConsultationCodes && { StepComponent: PaymentExtraStep, supportContent: getPaymentSupport() },
          { StepComponent: appointmentType === VIDEO_CONSULTATION ? ReasonStep : NotifCheckboxReasonStep, supportContent: getReasonStepSupport() },
        ].filter(Boolean);
      } else {
        stepConditionList = [
          selectedType,
          selectedPatient,
          desiredDateTime,
          isPaymentToggleStepValidated,
          selectedExpert,
          hasPayment ? Boolean(desiredConsultationCode) || isPayLater : 'SKIPPED', // only if payment is enabled
          additionalSelectedExpertList?.length > 0,
        ].filter(condition => condition !== 'SKIPPED');
        stepList = [
          { StepComponent: TypeStep, supportContent: getTypeStepSupport() },
          { StepComponent: PatientStep, supportContent: getPatientSupport() },
          { StepComponent: DateTimeStep, supportContent: getDateTimeStepSupport() },
          { StepComponent: PaymentToggleStep, supportContent: getPaymentSupport(), isAfterExpertStep: false },
          { StepComponent: AvailableExpertStep, supportContent: getAvailableExpertSupport() },
          hasPayment && hasExpertConsultationCodes && { StepComponent: PaymentExtraStep, supportContent: getPaymentSupport() },
          { StepComponent: MultipleAvailableExpertStep, supportContent: getMultipleAvailableExpertStepSupport() },
          { StepComponent: ReasonStep, supportContent: getReasonStepSupport() },
        ].filter(Boolean);
      }
      break;
    case FormTypes.LOCATION_FORM:
      stepConditionList = [
        selectedLocation,
        selectedPatient,
        selectedSlot,
        selectedExpert,
        (expertHasStripe && hasExpertConsultationCodes) ? isPaymentToggleStepValidated : 'SKIPPED',
      ].filter(condition => condition !== 'SKIPPED');
      stepList = [
        { StepComponent: LocationStep, supportContent: getLocationStepSupport() },
        { StepComponent: PatientStep, supportContent: getPatientSupport() },
        { StepComponent: SlotStep, supportContent: getSlotStepSupport() },
        { StepComponent: AvailableExpertStep, supportContent: getAvailableExpertSupport() },
        (expertHasStripe && hasExpertConsultationCodes) && { StepComponent: PaymentToggleStep, supportContent: getPaymentSupport(), isAfterExpertStep: true },
        { StepComponent: ReasonStep, supportContent: getReasonStepSupport() },
      ].filter(Boolean);
      break;
    case FormTypes.UNRESTRICTED_FORM:
      stepConditionList = []; // Not needed, form is always reset.
      stepList = [
        { StepComponent: DateTimeStep, supportContent: getDateTimeStepSupport(), shouldReset: true },
        { StepComponent: MultipleExpertStep, supportContent: getMultipleExpertStepSupport() },
        { StepComponent: ReasonStep, supportContent: getReasonStepSupport() },
      ];
      break;
    case FormTypes.PATIENT_FORM:
      stepConditionList = [isBoothToggleStepValidated, isBooth ? selectedLocation : false, selectedSlot, selectedExpert];
      stepList = [
        { StepComponent: BoothToggleStep, supportContent: getTypeStepSupport() },
        isBooth && { StepComponent: LocationStep, supportContent: getLocationStepSupport() },
        { StepComponent: SlotStep, supportContent: getSlotStepSupport() },
        { StepComponent: AvailableExpertStep, supportContent: getAvailableExpertSupport() },
        { StepComponent: ReasonStep, supportContent: getReasonStepSupport() },
      ].filter(Boolean);
      break;
  }
  const pendingStepIndex = stepConditionList.findIndex((stepCondition) => !stepCondition);
  return {
    stepList,
    initialStepIndex: pendingStepIndex >= 0 ? pendingStepIndex : stepConditionList.length,
    resetStepIndex: appointmentType === VIDEO_SPECIALIST_ASSESSMENT ? 0 : 1,
  };
};
